import { Link, Meta, Title } from "@solidjs/meta";
import { A, type RouteSectionProps, useLocation } from "@solidjs/router";
import { createSignal, For, type JSX, onCleanup, onMount, Show } from "solid-js";
import { isServer } from "solid-js/web";
import { MDXProvider } from "solid-mdx";
import { BlogNotFound, formatPostDate, labelTextClass, PostCover } from "~/components/marketing/BlogCards";
import { Byline, PostCta, RelatedPosts, ShareLinks } from "~/components/marketing/BlogPostChrome";
import { CompareCallout } from "~/components/marketing/CompareCallout";
import { OnThisPage, OnThisPageCollapsed } from "~/components/marketing/OnThisPage";
import { SectionHeading } from "~/components/marketing/SectionHeading";
import { getAppBaseUrl } from "~/lib/getAppBaseUrl";
import { getSocialCardImageUrl } from "~/lib/socialCard";
import { labelToSlug, normalizeLabels } from "./blogLabels";
import { postBySlug } from "./posts";
import "./(post)/blog-post.css";

const SubsectionHeading = (props: { children?: JSX.Element }) => (
  <SectionHeading level={3}>{props.children}</SectionHeading>
);

const organization = () => ({
  "@type": "Organization",
  name: "Chief",
  alternateName: "Storytell",
  url: getAppBaseUrl(),
});

export default function BlogPostLayout(props: RouteSectionProps) {
  const location = useLocation();
  const slug = () => location.pathname.replace(/^\/blog\//, "").replace(/\/$/, "");
  const meta = () => postBySlug.get(slug());
  const [lightboxImage, setLightboxImage] = createSignal<{ src: string; alt: string } | null>(null);

  const closeLightbox = () => setLightboxImage(null);

  const handlePostClick = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const image = target.closest("img");
    if (!(image instanceof HTMLImageElement)) return;
    if (image.closest("a")) return;

    const src = image.currentSrc || image.src;
    if (!src) return;

    setLightboxImage({ src, alt: image.alt ?? "" });
  };

  onMount(() => {
    if (isServer) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && lightboxImage()) {
        closeLightbox();
      }
    };

    window.addEventListener("keydown", handleEscape);
    onCleanup(() => window.removeEventListener("keydown", handleEscape));
  });

  return (
    <Show when={meta()} fallback={<BlogNotFound />}>
      {(m) => {
        const description = () => m().description ?? m().excerpt ?? "";
        const canonicalUrl = () => `${getAppBaseUrl()}/blog/${m().slug}`;
        const imageUrl = () => {
          const cover = m().coverImage;
          return cover ? new URL(cover, getAppBaseUrl()).href : getSocialCardImageUrl(getAppBaseUrl());
        };
        // Every `<` escaped, so a `</script>` in frontmatter can't close the tag early.
        const jsonLd = () =>
          JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: m().title,
            description: description(),
            datePublished: m().date,
            dateModified: m().lastUpdated ?? m().date,
            author: organization(),
            publisher: organization(),
            image: imageUrl(),
            mainEntityOfPage: canonicalUrl(),
            keywords: m().tags,
          }).replace(/</g, "\\u003c");
        return (
          <>
            <Title>{`${m().seoTitle ?? m().title} | Chief`}</Title>
            <Meta name="description" content={description()} />
            <Link rel="canonical" href={canonicalUrl()} />
            <Show when={m().draft}>
              <Meta name="robots" content="noindex" />
            </Show>
            <Meta property="og:title" content={m().title} />
            <Meta property="og:description" content={description()} />
            <Meta property="og:type" content="article" />
            <Meta property="og:url" content={canonicalUrl()} />
            <Meta property="og:site_name" content="Chief" />
            <Meta property="og:image" content={imageUrl()} />
            <Show when={m().coverImageAlt}>{(alt) => <Meta property="og:image:alt" content={alt()} />}</Show>
            <Meta property="article:published_time" content={m().date} />
            <Show when={m().lastUpdated}>
              {(updated) => <Meta property="article:modified_time" content={updated()} />}
            </Show>
            <Meta name="twitter:card" content="summary_large_image" />
            <Meta name="twitter:title" content={m().title} />
            <Meta name="twitter:description" content={description()} />
            <Meta name="twitter:image" content={imageUrl()} />
            <script type="application/ld+json" innerHTML={jsonLd()} />

            <Show when={m().draft}>
              <div
                role="status"
                class="border-b border-accent/40 bg-accent/10 px-4 py-2.5 text-center text-sm text-primary-text"
              >
                <strong>Draft, not published.</strong> This post shows only on preview deployments.
              </div>
            </Show>

            <div class="mx-auto w-full max-w-300 px-4 pb-16 sm:px-8">
              <header class="flex flex-col gap-4 pt-10">
                <A
                  href="/blog"
                  class="self-start text-sm text-secondary-text no-underline transition-colors hover:text-primary-text"
                >
                  ← Blog
                </A>
                <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <For each={normalizeLabels(m().labels)}>
                    {(label) => (
                      <A href={`/blog/${labelToSlug(label)}`} class={`${labelTextClass} no-underline hover:underline`}>
                        {label}
                      </A>
                    )}
                  </For>
                  <span class="text-sm text-secondary-text">
                    <time datetime={m().date}>{formatPostDate(m().date)}</time> · {m().readingTime} min read
                    <Show when={m().lastUpdated}>
                      {(updated) => (
                        <>
                          {" · "}Updated <time datetime={updated()}>{formatPostDate(updated())}</time>
                        </>
                      )}
                    </Show>
                  </span>
                </div>
                <h1 class="max-w-250 text-3xl leading-tight font-bold tracking-tight text-balance text-primary-text sm:text-5xl">
                  {m().title}
                </h1>
                <Show when={description()}>
                  <p class="max-w-210 text-lg leading-normal text-secondary-text sm:text-xl">{description()}</p>
                </Show>
                <div class="flex flex-col gap-4 pt-2 xl:hidden">
                  <Byline />
                  <OnThisPageCollapsed toc={m().toc} />
                </div>
              </header>

              <div class="mt-8 border-t border-primary-border pt-9 xl:grid xl:grid-cols-[200px_minmax(0,700px)_240px] xl:justify-between xl:gap-9">
                <aside class="sticky top-24 hidden flex-col gap-6 self-start xl:flex">
                  <Byline />
                  <ShareLinks url={canonicalUrl()} title={m().title} />
                </aside>
                <div class="min-w-0">
                  <Show when={m().coverImage || m().coverStat}>
                    <PostCover post={m()} class="mb-8 rounded-2xl border border-primary-border" />
                  </Show>
                  <article class="blog-post" onClick={handlePostClick}>
                    <MDXProvider components={{ h2: SectionHeading, h3: SubsectionHeading }}>
                      {props.children}
                    </MDXProvider>
                    <Show when={m().compare}>{(vendor) => <CompareCallout vendor={vendor()} />}</Show>
                  </article>
                  <div class="mt-12 flex flex-col gap-6 xl:hidden">
                    <ShareLinks url={canonicalUrl()} title={m().title} />
                    <PostCta />
                  </div>
                </div>
                <aside class="sticky top-24 hidden flex-col gap-8 self-start xl:flex">
                  <OnThisPage toc={m().toc} />
                  <PostCta />
                </aside>
              </div>

              <RelatedPosts post={m()} />
            </div>

            <Show when={lightboxImage()}>
              {(image) => (
                <div
                  class="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 sm:p-8"
                  role="dialog"
                  aria-modal="true"
                  aria-label={image().alt || "Expanded blog image"}
                  onClick={closeLightbox}
                >
                  <div
                    role="presentation"
                    class="flex w-[90vw] max-w-[90vw] flex-col items-end gap-3"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <button
                      type="button"
                      class="rounded-md border border-white/30 bg-black/50 px-3 py-1.5 text-sm text-white transition-colors hover:bg-black/70"
                      onClick={closeLightbox}
                    >
                      Close
                    </button>
                    <img
                      src={image().src}
                      alt={image().alt}
                      class="max-h-[85vh] w-full rounded-lg object-contain shadow-2xl"
                      loading="eager"
                    />
                  </div>
                </div>
              )}
            </Show>
          </>
        );
      }}
    </Show>
  );
}
